import { glowMote, hash01 } from './fx_kit'
import type { BossSpritePlacement } from './sprites'
import type { GameState } from './types'
import { cannonFajinPressure } from './executioner_cannon_fajin'
import { et } from './exec_tint'

// A: air drawn into the weapon-bearing shoulder. Filled, broken wisps and dust;
// no outline, muzzle orb, full-body halo, persistent history or wall-clock time.
// Coordinates belong to cannon_fajin_load (1024x1536), not a guessed world offset.
const clamp = (v: number): number => Math.max(0, Math.min(1, v))
const smooth = (v: number): number => { const p = clamp(v); return p * p * (3 - 2 * p) }

export function cannonFajinChargeProgress(state: GameState): number | null {
  const b = state.boss
  if (state.phase !== 'playing' || b.hp <= 0 || state.finisher || b.id !== 'the-executioner'
    || b.state !== 'telegraph' || b.currentAttack?.id !== 'exec_cannonFajin') return null
  const duration = b.currentAttack.chargeMs
  if (!(duration > 0) || !Number.isFinite(b.stateElapsed)) return null
  const p = b.stateElapsed * 1000 / duration
  return p >= 0 && p < 1 ? p : null
}

/** Draw in original load-art pixels. Caller supplies the SAME image transform as body. */
export function drawCannonFajinChargeArt(ctx: CanvasRenderingContext2D, progress: number, visibility = 1): void {
  if (!Number.isFinite(progress) || progress <= .10 || progress >= 1 || !(visibility > .01)) return
  const p = clamp((progress - .10) / .90)
  // Gentle entry, strongest at ~3/4, then all material is swallowed before contact.
  const envelope = smooth(p / .23) * (1 - smooth((p - .87) / .13)) * clamp(visibility)
  if (envelope <= .001) return
  const compression = smooth((p - .35) / .65)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 28; i++) {
    const seed = hash01(i + 18), angle = hash01(i + 103) * Math.PI * 2
    const cycle = p * (1.15 + hash01(i + 251) * .55) + seed
    const q = cycle - Math.floor(cycle)
    const life = Math.sin(q * Math.PI) ** 1.5
    const radius = (510 + hash01(i + 63) * 260) * (1 - .3 * compression)
    const targetX = 375 + hash01(i + 305) * 110
    const targetY = 290 + hash01(i + 351) * 100
    // Curved paths vary individually; packets accelerate toward the shoulder.
    const at = (u: number): { x: number; y: number } => {
      const r = radius * (1 - u * u)
      const a = angle + (1 - u) * (seed - .5) * .65
      return { x: targetX + Math.cos(a) * r, y: targetY + Math.sin(a) * r * .68 }
    }
    const head = at(q), tail = at(Math.max(0, q - .035))
    const direction = Math.atan2(head.y - tail.y, head.x - tail.x)
    const alpha = envelope * life * (.18 + .32 * compression)
    // Three separated soft patches form each short wisp, never a continuous spoke.
    for (let j = 0; j < 3; j++) {
      const pos = at(Math.max(0, q - j * .022))
      ctx.save()
      ctx.translate(pos.x, pos.y)
      ctx.rotate(direction)
      ctx.scale(2.1 + q * 1.6, .65)
      glowMote(ctx, 0, 0, 9 + hash01(i + 87) * 7, et('parry', '#c8b0ec'), alpha * (1 - j * .27))
      ctx.restore()
    }
    // Sparse grains give the air travel direction at actual battle size.
    if (i % 3 === 0) glowMote(ctx, head.x, head.y, 5 + 3 * compression, et('parry', '#f0e8ff'), alpha * .8)
  }
  ctx.restore()
}

/** The same pressure as the body drives intake time and compression around the shoulder. */
export function drawSyncedCannonFajinChargeArt(ctx: CanvasRenderingContext2D, progress: number, visibility = 1): void {
  if (!Number.isFinite(progress) || progress < 0 || progress >= 1 || !(visibility > .01)) return
  const { pressure } = cannonFajinPressure(progress * 900)
  if (pressure <= 0) return
  ctx.save()
  ctx.translate(430, 345)
  const squeeze = 1 - .25 * pressure
  ctx.scale(squeeze, squeeze)
  ctx.translate(-430, -345)
  drawCannonFajinChargeArt(ctx, .10 + .79 * pressure, visibility)
  ctx.restore()
}

/** Reuse the sprite's final rect/flip/rotation, including foot lift and camera placement. */
export function drawCannonFajinCharge(ctx: CanvasRenderingContext2D, placement: BossSpritePlacement | null,
  progress: number | null, visibility = 1): void {
  if (progress === null || !placement || placement.imageKey !== 'cannon_fajin_load') return
  const { pivot, imageRect: r } = placement
  ctx.save()
  ctx.translate(pivot.x, pivot.y)
  ctx.scale(placement.facingMul, 1)
  ctx.rotate(placement.rotationRad)
  ctx.translate(r.x - pivot.x, r.y - pivot.y)
  ctx.scale(r.width / 1024, r.height / 1536)
  drawSyncedCannonFajinChargeArt(ctx, progress, visibility)
  ctx.restore()
}
